import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  IconPhotoUp, IconPlayerPlay, IconSearch, IconSortAscending, IconSortDescending, IconUserPlus,
} from '@tabler/icons-react'
import {apiGet} from './api/client'
import {useApiToken} from './api/useApiToken'
import {findGroupTag, isMedia, isVideo, looksVideo, mediaDate, sizeLimit, uploadPhoto} from './api/media'
import {fullName, mediaSortKey, nameMatches, searchWords} from './api/tree'
import type {TreeIndex} from './api/types'
import MediaViewer from './MediaViewer'
import Portrait from './Portrait'

interface MediaItem {
  handle: string
  gramps_id: string
  desc?: string
  mime?: string
  path?: string
  /** дата съёмки — собственное поле медиа в Gramps, как у события */
  date?: {dateval?: number[]}
  /** метки; нас интересует одна — «групповое фото» */
  tag_list?: string[]
}

interface Props {
  ix: TreeIndex
  onOpenPerson: (h: string) => void
  /** снимок могли привязать или отвязать — древо надо перечитать */
  onChanged: () => void
}

/** Что показываем. «Групповые» — новый класс: снимок принадлежит не одному. */
type Filter = 'all' | 'group' | 'solo' | 'loose' | 'video'

const FILTERS: [Filter, string][] = [
  ['all', 'Все'],
  ['group', 'Групповые'],
  ['solo', 'Портреты'],
  ['loose', 'Без привязки'],
  ['video', 'Видео'],
]

/** Секция ленты: люди — по одной на каждого, плюс общая для неприкаянных. */
interface Section {
  key: string
  /** null у секции «без привязки» */
  person: string | null
  items: MediaItem[]
}

export default function MediaGrid({ix, onOpenPerson, onChanged}: Props) {
  const [items, setItems] = useState<MediaItem[] | null>(null)
  const [err, setErr] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [q, setQ] = useState('')
  /** открытый на весь экран снимок — индекс в плоском списке видимого */
  const [shown, setShown] = useState<number | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [adding, setAdding] = useState(0)
  /** handle метки «групповое фото»; узнаём при первой надобности */
  const [groupTag, setGroupTag] = useState<string | null>(null)
  /** выбранный род; пусто — все */
  const [clan, setClan] = useState('')
  /** порядок в лентах: от старых к новым или наоборот */
  const [newest, setNewest] = useState(false)
  const jwt = useApiToken()

  useEffect(() => {
    let alive = true
    apiGet<MediaItem[]>('/media/', {pagesize: 20000})
      .then(m => alive && setItems(m))
      .catch(e => alive && setErr(e instanceof Error ? e.message : 'Не удалось загрузить'))
    // метку читаем сразу: иначе уже помеченные снимки не опознаются,
    // пока в этом сеансе не пометят ещё один
    findGroupTag().then(h => alive && setGroupTag(h)).catch(() => {})
    return () => { alive = false }
  }, [])

  /* Чей снимок — обратной картой из людей: у медиа обратных ссылок
     в списке нет, а отдельный запрос на каждый файл — это сотня запросов. */
  const owners = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const p of ix.people.values()) {
      for (const r of p.media_list || []) {
        const list = map.get(r.ref)
        if (list) list.push(p.handle)
        else map.set(r.ref, [p.handle])
      }
    }
    return map
  }, [ix])

  /** Групповой — либо привязано двое и больше, либо стоит метка. */
  const isGroup = useCallback((m: MediaItem) => {
    if ((owners.get(m.handle) || []).length > 1) return true
    return !!groupTag && (m.tag_list || []).includes(groupTag)
  }, [owners, groupTag])

  const counts = useMemo(() => {
    const c = {all: 0, group: 0, solo: 0, loose: 0, video: 0}
    for (const m of items || []) {
      const n = (owners.get(m.handle) || []).length
      c.all++
      if (looksVideo(m.mime)) c.video++
      if (isGroup(m)) c.group++
      else if (n === 0) c.loose++
      else c.solo++
    }
    return c
  }, [items, owners, isGroup])

  /**
   * Роды на снимке — фамилии тех, кто на нём отмечен.
   *
   * Род определяем по фамилии из ОСНОВНОГО имени, а им у всех женщин в
   * базе стоит имя при рождении — то есть девичья фамилия. Это и нужно:
   * род — это откуда человек родом, а не куда вышел замуж. В списках при
   * этом показывается замужняя фамилия с девичьей в скобках, но отбор
   * идёт по девичьей.
   */
  const clansOf = useCallback((m: MediaItem): string[] => {
    const out: string[] = []
    for (const h of owners.get(m.handle) || []) {
      const s = (ix.people.get(h)?.profile?.name_surname || '').trim()
      if (s && !out.includes(s)) out.push(s)
    }
    return out
  }, [owners, ix])

  /** Список родов со счётчиками — по убыванию, чтобы частые были сверху. */
  const clans = useMemo(() => {
    const c = new Map<string, number>()
    for (const m of items || []) for (const s of clansOf(m)) c.set(s, (c.get(s) || 0) + 1)
    return [...c.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ru'))
  }, [items, clansOf])

  /** Отбор по кнопкам и поиску: поиск идёт и по подписи, и по людям на снимке. */
  const picked = useMemo(() => {
    const words = searchWords(q)
    return (items || []).filter(m => {
      const who = owners.get(m.handle) || []
      if (filter === 'loose' && (who.length || isGroup(m))) return false
      if (filter === 'solo' && (who.length !== 1 || isGroup(m))) return false
      if (filter === 'group' && !isGroup(m)) return false
      if (filter === 'video' && !looksVideo(m.mime)) return false
      if (clan && !clansOf(m).includes(clan)) return false
      if (!words.length) return true
      const caption = (m.desc || '').toLowerCase()
      if (words.every(w => caption.includes(w))) return true
      return who.some(h => nameMatches(ix.people.get(h), words))
    })
  }, [items, owners, filter, q, ix, isGroup, clan, clansOf])

  /**
   * Раскладка по людям вместо общей кучи.
   *
   * Групповой снимок попадает в секцию каждого, кто на нём: файл один,
   * появлений несколько. Иначе с двумя десятками общих фотографий
   * лента превращается в свалку, где ничего не найти.
   */
  const sections = useMemo(() => {
    const loose: MediaItem[] = []
    const byPerson = new Map<string, MediaItem[]>()
    for (const m of picked) {
      const who = owners.get(m.handle) || []
      if (!who.length) { loose.push(m); continue }
      for (const h of who) {
        const list = byPerson.get(h)
        if (list) list.push(m)
        else byPerson.set(h, [m])
      }
    }
    /* Хронология внутри каждой ленты. Снимки без даты уходят в конец
       независимо от направления: неизвестное время не должно вклиниваться
       между известными и рвать порядок. */
    const bySort = (a: MediaItem, b: MediaItem) => {
      // берём дату из своих данных, а не из индекса древа: после правки
      // даты лента перечитывается сразу, а индекс — только со всем древом
      const sa = mediaSortKey(a.date?.dateval)
      const sb = mediaSortKey(b.date?.dateval)
      if (!sa || !sb) return sa === sb ? 0 : sa ? -1 : 1
      return newest ? sb - sa : sa - sb
    }

    const out: Section[] = []
    // неприкаянные — первыми: это рабочая очередь, её надо видеть
    if (loose.length) out.push({key: 'loose', person: null, items: loose.sort(bySort)})
    const people = [...byPerson.keys()].sort((a, b) =>
      fullName(ix.people.get(a)).localeCompare(fullName(ix.people.get(b)), 'ru'))
    for (const h of people) out.push({key: h, person: h, items: byPerson.get(h)!.sort(bySort)})
    return out
  }, [picked, owners, ix, newest])

  /** Плоский порядок для стрелок в просмотрщике — тот же, что на экране. */
  const flat = useMemo(
    () => sections.flatMap(s => s.items.map(m => ({m, section: s.key}))),
    [sections],
  )

  /* Вставка из буфера. Старые снимки чаще всего приходят не файлом, а
     кадром из мессенджера или обрезком из просмотрщика — сохранять их
     на диск только чтобы выбрать в диалоге, лишний шаг. */
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      // при открытом просмотре вставка принадлежит ему, а не ленте
      if (shown !== null) return
      const imgs = [...(e.clipboardData?.items || [])]
        .filter(i => i.kind === 'file' && i.type.startsWith('image/'))
        .map(i => i.getAsFile())
        .filter((f): f is File => !!f)
      if (!imgs.length) return
      e.preventDefault()
      upload(imgs)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  })

  async function upload(files: Blob[]) {
    const bad = files.find(b => !isMedia(b) || b.size > sizeLimit(b))
    if (bad) {
      const mb = Math.round(sizeLimit(bad) / 1048576)
      setErr(isMedia(bad)
        ? `Файл тяжелее ${mb} МБ — столько ${isVideo(bad) ? 'семейный ролик' : 'снимок'} не весит`
        : 'Годятся картинки и видео')
      return
    }
    setErr('')
    setAdding(files.length)
    try {
      for (const f of files) {
        await uploadPhoto(f)
        setAdding(n => n - 1)
      }
      const fresh = await apiGet<MediaItem[]>('/media/', {pagesize: 20000})
      setItems(fresh)
      // новые снимки ещё ни к кому не привязаны — сразу показываем их
      setFilter('loose')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось загрузить')
    } finally {
      setAdding(0)
    }
  }

  if (err && !items) return <div className="page"><div className="err inline">{err}</div></div>
  if (!items) return <div className="page"><div className="muted">Загружаем…</div></div>

  return (
    <div className="page">
      <div className="pagehead">
        <h1>Фото и документы</h1>
        <div className="muted">
          {picked.length === items.length
            ? `${items.length} снимков`
            : `${picked.length} из ${items.length}`}
        </div>
      </div>

      <div className="toolbar">
        <div className="search wide">
          <IconSearch size={15} className="ico" />
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Подпись или имя человека на снимке" />
        </div>
        <div className="evfilter">
          {FILTERS.map(([f, label]) => (
            <button key={f} className={'chip' + (filter === f ? ' on' : '')}
                    onClick={() => setFilter(f)}>
              {label} <em>{counts[f]}</em>
            </button>
          ))}
        </div>
        <button className="btn" title="Порядок снимков по дате съёмки"
                onClick={() => setNewest(v => !v)}>
          {newest ? <IconSortDescending size={15} stroke={1.7} />
                  : <IconSortAscending size={15} stroke={1.7} />}
          {newest ? 'Сначала новые' : 'Сначала старые'}
        </button>
        {clans.length > 0 && (
          <select className="sel clanpick" value={clan} onChange={e => setClan(e.target.value)}
                  title="Показать снимки одного рода">
            <option value="">Все роды</option>
            {clans.map(([name, n]) => (
              <option key={name} value={name}>{name} ({n})</option>
            ))}
          </select>
        )}
        <div className="spacer" />
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden
               onChange={e => { upload([...(e.target.files || [])]); e.target.value = '' }} />
        <button className="btn primary" disabled={adding > 0}
                onClick={() => fileRef.current?.click()}
                title="Или просто вставьте снимок из буфера: Ctrl+V">
          <IconPhotoUp size={15} stroke={1.7} />
          {adding > 0 ? `Загружаем… осталось ${adding}` : 'Загрузить фото или видео'}
        </button>
      </div>

      <span className="hint" style={{display: 'block', marginBottom: 12}}>
        Снимок можно вставить прямо из буфера — <b>Ctrl+V</b>, никуда не нажимая.
        Видео берём файлом: до 300 МБ, перемотка работает.
      </span>

      {err && <div className="err inline" style={{marginBottom: 12}}>{err}</div>}

      {!sections.length && <div className="empty">Ничего не нашлось</div>}

      {sections.map(s => (
        <div className="album" key={s.key}>
          <div className="albhead">
            {s.person ? (
              <button type="button" className="albwho" onClick={() => onOpenPerson(s.person!)}>
                <Portrait p={ix.people.get(s.person)} size={34} shape="rounded" />
                <span>{fullName(ix.people.get(s.person))}</span>
              </button>
            ) : (
              <span className="albwho loose">Без привязки</span>
            )}
            <span className="muted">{s.items.length}</span>
          </div>

          <div className="grid">
            {s.items.map(m => {
              const who = owners.get(m.handle) || []
              return (
                <div className={'tile' + (who.length ? '' : ' loose')} key={s.key + m.handle}>
                  {/* Плитка — не кнопка целиком: внутрь вложены свои
                      кнопки действий, а кнопка в кнопке ломает клики. */}
                  <button type="button" className="tileopen"
                          onClick={() => setShown(flat.findIndex(
                            x => x.m.handle === m.handle && x.section === s.key))}>
                    {looksVideo(m.mime) ? (
                      /* Своих миниатюр у видео нет: сервер строит их
                         картинкой и на ролике спотыкается. Берём первый
                         кадр самого файла — preload=metadata и #t=0.1
                         заставляют браузер его показать, не качая всё. */
                      <span className="vthumb">
                        <video src={`/api/media/${m.handle}/file?jwt=${jwt}#t=0.1`}
                               preload="metadata" muted playsInline />
                        <span className="playmark"><IconPlayerPlay size={22} stroke={2} /></span>
                      </span>
                    ) : (
                      <img src={`/api/media/${m.handle}/thumbnail/320?jwt=${jwt}`}
                           alt="" loading="lazy" />
                    )}
                    <span className="cap">
                      <span className="captext">{m.desc || m.gramps_id}</span>
                      {/* Дата целиком, а не только год: «14.05.1979» отвечает
                          на вопрос сразу, а год заставляет открывать снимок. */}
                      {mediaDate(m) && <span className="datemark">{mediaDate(m)}</span>}
                    </span>
                  </button>
                  {isGroup(m) && (
                    <span className="grp">{who.length > 1 ? `${who.length} чел.` : 'группа'}</span>
                  )}
                  {/* Разбирают снимки чаще всего именно с плитки, и гонять
                      ради привязки в полноэкранный просмотр — лишний шаг. */}
                  <button type="button" className="drop link" title="Кто на снимке, когда и что"
                          onClick={() => setShown(flat.findIndex(x => x.m.handle === m.handle))}>
                    <IconUserPlus size={13} stroke={1.9} />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {shown !== null && flat[shown] && (
        <MediaViewer
          ix={ix}
          handles={flat.map(x => x.m.handle)}
          index={shown}
          onIndex={setShown}
          onClose={() => setShown(null)}
          onOpenPerson={onOpenPerson}
          onChanged={async () => {
            setItems(await apiGet<MediaItem[]>('/media/', {pagesize: 20000}))
            onChanged()
          }}
        />
      )}

    </div>
  )
}
